import { useEffect, useMemo, useRef, useState } from 'react';
import { decodeWaveform, formatDuration } from '@vav/protocol';
import type { AttachmentRef } from '@vav/protocol';
import { useT } from '../i18n.tsx';

/**
 * Voice note player.
 *
 * The waveform is drawn from peaks the sender computed, so the bars appear
 * immediately — before the clip has been downloaded or decoded. Clicking a bar
 * seeks, which is the one interaction people actually use on a voice message.
 */
export function VoicePlayer({ url, attachment }: { url?: string; attachment: AttachmentRef }) {
  const t = useT();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState((attachment.durationMs ?? 0) / 1000);

  const peaks = useMemo(() => decodeWaveform(attachment.waveform), [attachment.waveform]);
  const progress = duration > 0 ? position / duration : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setPosition(audio.currentTime);
    const onEnd = () => {
      setPlaying(false);
      setPosition(0);
    };
    const onMeta = () => {
      // A stream-recorded WebM often reports Infinity until it has been seeked;
      // fall back to the duration the sender measured.
      if (Number.isFinite(audio.duration) && audio.duration > 0) setDuration(audio.duration);
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('loadedmetadata', onMeta);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('loadedmetadata', onMeta);
    };
  }, [url]);

  function toggle() {
    const audio = audioRef.current;
    if (!audio || !url) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      void audio.play().then(() => setPlaying(true));
    }
  }

  function seek(event: React.MouseEvent<HTMLDivElement>) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    // The thread is RTL, but the waveform reads left-to-right like the audio.
    const ratio = (event.clientX - rect.left) / rect.width;
    audio.currentTime = Math.max(0, Math.min(1, ratio)) * duration;
  }

  const remaining = playing || position > 0 ? duration - position : duration;

  return (
    <div className="voice">
      <button
        className="voice__play"
        onClick={toggle}
        disabled={!url}
        aria-label={playing ? t('media.pause') : t('media.play')}
      >
        {!url ? '…' : playing ? '❚❚' : '▶'}
      </button>

      <div className="voice__wave" onClick={seek} role="slider" aria-label={t('media.seek')} tabIndex={0}>
        {peaks.map((peak, index) => (
          <span
            key={index}
            className={`voice__bar ${index / peaks.length <= progress ? 'is-played' : ''}`}
            // A floor of 12% keeps silence visible as a line rather than a gap.
            style={{ height: `${Math.max(12, (peak / 255) * 100)}%` }}
          />
        ))}
      </div>

      <time className="voice__time">{formatDuration(remaining * 1000)}</time>
      {url && <audio ref={audioRef} src={url} preload="metadata" />}
    </div>
  );
}

/** Inline video, with the poster sized from the dimensions the sender sent. */
export function VideoPlayer({ url, attachment }: { url?: string; attachment: AttachmentRef }) {
  const t = useT();
  const ratio =
    attachment.width && attachment.height ? `${attachment.width}/${attachment.height}` : '16/9';

  if (!url) {
    return (
      <div className="videoWrap" style={{ aspectRatio: ratio }}>
        <div className="imageWrap__loading">{t('media.decrypting')}</div>
      </div>
    );
  }

  return (
    <div className="videoWrap" style={{ aspectRatio: ratio }}>
      {/* preload="metadata" so the poster frame appears without pulling the
          whole clip — attachments are already decrypted in memory here. */}
      <video src={url} controls preload="metadata" playsInline />
    </div>
  );
}

/** An attached audio file — a song, a recording forwarded from elsewhere. */
export function AudioPlayer({ url, attachment }: { url?: string; attachment: AttachmentRef }) {
  const t = useT();
  return (
    <div className="audioCard">
      <div className="audioCard__head">
        <span className="fileCard__icon">🎵</span>
        <span className="audioCard__name">
          {attachment.name}
        </span>
      </div>
      {url ? <audio src={url} controls preload="metadata" /> : <span className="muted">{t('media.decrypting')}</span>}
    </div>
  );
}
